"use client"
import { useState, useEffect, useContext, useRef, useMemo } from "react"
import { formatNumberForDisplay, getLocaleConfig } from "../../utils/numeros.js"
import { DataGrid, GridToolbarColumnsButton } from "@mui/x-data-grid"
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Chip,
  alpha,
  Tooltip,
  Collapse, // Added for collapsible sections
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import CustomPagination from "./CustomPagination"
import ReplayIcon from "@mui/icons-material/Replay"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import ReceiptIcon from "@mui/icons-material/Receipt"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import { ErpContext } from "../../context/ErpContext.js"
import "./TablaVentasComponent.css"
import TransaccionModal from "./TransaccionModal.js"

import dayjs from "dayjs"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { deliverPdf, reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"
import { drawPdfLogo } from "../../context/pdf_esqueletos/helpers/pdfLogo.js"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"

import SpinnerLading from "../misc/SpinnerComponent.js"

const ColumnSelectorToolbar = () => (
  <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, py: 0.5 }}>
    <GridToolbarColumnsButton />
  </Box>
)

const CajaComponent = () => {
  const theme = useTheme()
  
  const {
    user,
    getTransaccionesPagoFilter,
    getTipoMedioDePago,
    tipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    isNavbarCollapsed,
    getAndPostCajaDatos,
    getFechaHoraActualClient,
    getUbicacion,
    ubicaciones,
    getUsuario,
    usuarios,
    formatoNumericoGlobal,
    tieneAccesoFuncionalidad,
    getParametrosGlobales,
    // getCajaAndRecalcularCaja,
  } = useContext(ErpContext)

  // Permisos granulares (TRANSACCIONES10 - Caja)
  const mostrarMontoInicial = tieneAccesoFuncionalidad(user, "TRANSACCIONES10", "TRANSACCIONES10_verMontoInicial")
  const mostrarMontoFinal = tieneAccesoFuncionalidad(user, "TRANSACCIONES10", "TRANSACCIONES10_verMontoFinal")

  // Configuración de locale numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // Filtrar ubicaciones que NO son destinos (esDestino = false) - consistente con transacciones de caja
  const ubicacionesNoDestino = useMemo(
    () => (ubicaciones || []).filter((u) => u.esDestino === false),
    [ubicaciones]
  )

  const isFirstRender = useRef(true)
  const DEFAULT_SORT = [{ field: "fechaTransaccionTimestamp", sort: "desc" }]

  const today = dayjs()
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [transaccionesPagos, setTransaccionesPagos] = useState({})
  const [cajaData, setCajaData] = useState(null)

  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [selectedUsuario, setSelectedUsuario] = useState(null)
  const [cajaMontoFinal, setCajaMontoFinal] = useState(0)
  const [totalIngresos, setTotalIngresos] = useState(0)
  const [totalEgresos, setTotalEgresos] = useState(0)
  const [loading, setLoading] = useState(true)
  const [sortModel, setSortModel] = useState(DEFAULT_SORT)

  const [showPaymentFilters, setShowPaymentFilters] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [agruparMediosPago, setAgruparMediosPago] = useState(false)
  const [mediosDePagoAgrupados, setMediosDePagoAgrupados] = useState([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ id: false, tipoTransaccion: false })

  const contarFiltrosActivos = () => {
    let count = 0
    if (selectedTipoMedioDePago) count++
    if (selectedMedioDePago && selectedMedioDePago.id) count++
    if (selectedUbicacion) count++
    if (selectedUsuario) count++
    return count
  }

  const increaseDate = () =>
    setStartDate((prev) => {
      const next = prev ? prev.add(1, "day") : dayjs()
      // si next es posterior a hoy, no avanzamos
      return next.isAfter(dayjs(), "day") ? prev : next
    })

  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))
  useEffect(() => {
    // Aquí puedes llamar a la función
    fetchInitialData()
  }, [])

  const fetchInitialData = async () => {
    const today = dayjs()
    setStartDate(today)
    setEndDate(today)
    await getTipoMedioDePago("verEnCaja=true")
    await getUbicacion()
    await getUsuario()
  }

  useEffect(() => {
    // Aquí puedes llamar a la función
    fetchData()
  }, [startDate])

  const fetchData = async (overrides = {}) => {
    setLoading(true)
    const { fechaServidor, zona } = getFechaHoraActualClient(startDate)
    console.log("fechaServidor: ", fechaServidor)
    console.log("zona: ", zona)
    
    // Pasar ubicación seleccionada o 'todas' si no hay filtro
    const ubicacionId = selectedUbicacion ? selectedUbicacion.id : 'todas'
    console.log("ubicacionId: ", ubicacionId)
    
    const cajaPenUltimoDia = await getAndPostCajaDatos(
      dayjs(fechaServidor).tz(zona).add(-1, "day"), 
      zona,
      ubicacionId
    )
    
    // Procesar respuesta: puede tener múltiples ubicaciones
    let montoCierreCalculado = 0
    
    if (cajaPenUltimoDia && cajaPenUltimoDia.ubicaciones) {
      // Sumar montos de todas las ubicaciones si no hay filtro específico
      if (!selectedUbicacion && cajaPenUltimoDia.ubicaciones.length > 1) {
        const montoTotalTodasUbicaciones = cajaPenUltimoDia.ubicaciones.reduce((sum, ub) => {
          return sum + (ub.ultimaCaja?.montoCierre || 0)
        }, 0)
        
        montoCierreCalculado = montoTotalTodasUbicaciones
        
        setCajaData({
          ubicaciones: cajaPenUltimoDia.ubicaciones,
          ultimaCaja: {
            montoCierre: montoTotalTodasUbicaciones,
            descripcion: 'Todas las ubicaciones'
          }
        })
      } else if (cajaPenUltimoDia.ubicaciones.length === 1) {
        // Una sola ubicación (filtrada o única)
        montoCierreCalculado = cajaPenUltimoDia.ubicaciones[0].ultimaCaja?.montoCierre || 0
        
        setCajaData({
          ubicaciones: cajaPenUltimoDia.ubicaciones,
          ultimaCaja: cajaPenUltimoDia.ubicaciones[0].ultimaCaja
        })
      } else {
        // Sin ubicaciones
        montoCierreCalculado = 0
        
        setCajaData({
          ubicaciones: [],
          ultimaCaja: { montoCierre: 0 }
        })
      }
    } else {
      // Formato antiguo o error
      montoCierreCalculado = cajaPenUltimoDia?.ultimaCaja?.montoCierre || 0
      setCajaData(cajaPenUltimoDia)
    }

    console.log("fechaServidor: ", fechaServidor)
    const ed = endDate ? dayjs(endDate) : null

    const tipoMedioDePagoId = selectedTipoMedioDePago ? selectedTipoMedioDePago.id : null
    const medioDePagoId = selectedMedioDePago ? selectedMedioDePago.id : null
    const ubicacionFiltro = selectedUbicacion ? selectedUbicacion.id : null
    const usuarioId = selectedUsuario ? selectedUsuario.id : null

    const nextPageRaw = overrides.page ?? page
    const nextPage = Math.max(1, Number(nextPageRaw) || 1)

    const sortFieldFromParam = overrides.sortFieldParam || null
    const sortOrderFromParam = overrides.sortOrderParam || null
    const activeSort = Array.isArray(sortModel) && sortModel.length ? sortModel[0] : DEFAULT_SORT[0]
    const sortField = sortFieldFromParam || activeSort?.field || "fechaTransaccionTimestamp"
    const sortOrder = sortOrderFromParam || (activeSort?.sort === "asc" ? "asc" : "desc")

    const data = await getTransaccionesPagoFilter(
      fechaServidor,
      nextPage,
      tipoMedioDePagoId,
      medioDePagoId,
      zona,
      ubicacionFiltro,
      usuarioId,
      sortField,
      sortOrder,
    )
    console.log(data)

    console.log("data: ", data)
    const currentPage = data.currentPage || 0
    setPage(currentPage)
    setTotalPages(data.totalPages)
    setTotalCount(data.total)
    // ✅ Totales globales del backend (todas las páginas)
    const totalIngresosGlobal = data.totalesGlobales?.totalIngresos || 0
    const totalEgresosGlobal = data.totalesGlobales?.totalEgresos || 0
    const montoNetoGlobal = data.totalesGlobales?.montoNeto || 0
    
    // ✅ Datos agrupados por medio de pago
    setMediosDePagoAgrupados(data.mediosDePagoAgrupados || [])

    const transaccionTransformed = data.results || []

    // 🧩 Compatibilidad + lógica lutente: solo mostrar transacciones con verEnColumna Ingreso/Egreso
    const transaccionesConVerEnCaja = transaccionTransformed.filter((transaccion) => {
      const tipo = transaccion?.transaccion?.tipoTransaccion
      return tipo?.verEnColumna === "Ingreso" || tipo?.verEnColumna === "Egreso"
    })

    // Totales de la página actual (para fila "TOTALES" dentro de la grilla): usar operacionCaja
    let totalIngresosPagina = 0
    let totalEgresosPagina = 0

    transaccionesConVerEnCaja.forEach((transaccion) => {
      const monto = transaccion?.pago?.montoTotal || 0
      const op = transaccion?.transaccion?.tipoTransaccion?.operacionCaja
      if (op === "+") totalIngresosPagina += Math.abs(monto)
      else if (op === "-") totalEgresosPagina += Math.abs(monto)
    })

    // Usar el monto de cierre calculado anteriormente + neto global
    const montoFinalDato = montoCierreCalculado + montoNetoGlobal

    setMontoTotalTransacciones(montoNetoGlobal)
    setCajaMontoFinal(montoFinalDato)
    setTotalIngresos(totalIngresosGlobal)
    setTotalEgresos(totalEgresosGlobal)

    const transaccionPagoTransformed = transaccionesConVerEnCaja.map((transaccion) => {
      const pagos = [
        {
          descripcion: transaccion.pago.medioDePago.descripcion,
          monto: transaccion.pago.montoTotal,
        },
      ]

     const fechaBase = transaccion?.transaccion?.fecha ? new Date(transaccion.transaccion.fecha) : null
      const fechaTimestamp = fechaBase && !isNaN(fechaBase.getTime()) ? fechaBase.getTime() : null
      const formattedDate = fechaBase ? new Intl.DateTimeFormat("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(fechaBase) : ""

      return {
        id: `${transaccion.idTransaccion} - ${transaccion.idPago}`,
        fechaTransaccionTimestamp: fechaTimestamp,
        createdAt: formattedDate,
        montoTotal: Math.abs(transaccion.pago.montoTotal), //Porque buscamos cada pago.
        pagos: pagos,
        tipoTransaccion: transaccion.transaccion.tipoTransaccion.descripcion,
        idTipoTransaccion: transaccion.transaccion.idTipoTransaccion,
        operacionCaja: transaccion.transaccion.tipoTransaccion.operacionCaja,
        verEnColumna:
          transaccion.transaccion.tipoTransaccion.verEnColumna ||
          (transaccion.transaccion.tipoTransaccion.operacionCaja === "+"
            ? "Ingreso"
            : transaccion.transaccion.tipoTransaccion.operacionCaja === "-"
              ? "Egreso"
              : null),
        ubicacion: transaccion.transaccion.ubicacion?.descripcion || "Sin ubicación",
        usuario: transaccion.transaccion.usuario?.usuario || "Sin usuario",
        entidad: (() => {
          const ent = transaccion.transaccion?.entidad
          if (!ent) return "Sin entidad"
          const parts = [ent.descripcion, ent.apellido].filter(Boolean)
          return parts.length > 0 ? parts.join(" ") : "Sin entidad"
        })(),
      }
    })

    const totalRow = {
      id: "TOTAL_ROW",
      fechaTransaccionTimestamp: null,
      createdAt: "",
      montoTotal: 0,
      pagos: [],
      tipoTransaccion: "TOTALES",
      idTipoTransaccion: null,
      operacionCaja: "TOTAL",
      verEnColumna: null,
      ubicacion: "",
      usuario: "",
      entidad: "",
      totalIngresos: totalIngresosPagina,
      totalEgresos: totalEgresosPagina,
      isTotal: true,
    }

    setTransaccionesPagos([...transaccionPagoTransformed, totalRow])
    setLoading(false)
  }

  const handleSortModelChange = (newModel) => {
    const nextSort = Array.isArray(newModel) && newModel.length ? newModel[0]?.sort : "desc"
    const normalizedSort = nextSort === "asc" ? "asc" : "desc"
    const nextModel = [{ field: "fechaTransaccionTimestamp", sort: normalizedSort }]
    setSortModel(nextModel)
    setPage(1)
    fetchData({ page: 1, sortFieldParam: "fechaTransaccionTimestamp", sortOrderParam: normalizedSort })
  }

  const handleTransactionAdded = () => {
    setIsModalOpen(false)
    fetchData()
  }

  // Columnas para vista agrupada por medio de pago
  const columnsAgrupadas = useMemo(() => [
    {
      field: "medioDePago",
      headerName: "Medio de Pago",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3",
                  fontSize: "1.05rem",
                }}
              >
                TOTALES
              </Typography>
            </Box>
          )
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PointOfSaleIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "totalIngresos",
      headerName: "Ingreso Total",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                bgcolor: params.row.isTotal ? "transparent" : alpha("#4CAF50", 0.1),
                color: params.row.isTotal ? "#2196F3" : "#4CAF50",
                fontSize: params.row.isTotal ? "1.05rem" : "inherit",
              }}
            >
              ${formatNumberForDisplay(params.value || 0, { locale: localeConfig.locale })}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "totalEgresos",
      headerName: "Egreso Total",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: params.row.isTotal ? "#2196F3" : "inherit",
                fontSize: params.row.isTotal ? "1.05rem" : "inherit",
              }}
            >
              ${formatNumberForDisplay(params.value || 0, { locale: localeConfig.locale })}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "montoNeto",
      headerName: "Monto Neto",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: false,
      renderCell: (params) => {
        const isNegative = params.value < 0
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: params.row.isTotal 
                  ? "#2196F3" 
                  : isNegative 
                    ? "#F44336" 
                    : "#4CAF50",
                fontSize: params.row.isTotal ? "1.05rem" : "inherit",
              }}
            >
              ${formatNumberForDisplay(params.value || 0, { locale: localeConfig.locale })}
            </Typography>
          </Box>
        )
      },
    },
  ], [localeConfig.locale])

  const columns = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3",
                  fontSize: "1.05rem",
                }}
              >
                TOTALES
              </Typography>
            </Box>
          )
        }

        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "fechaTransaccionTimestamp",
      headerName: "Fecha",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: true,
      renderCell: (params) => {
        if (params.row.isTotal) return null
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.row.createdAt}</Typography>
          </Box>
        )
      },
    },
    {
      field: "entidad",
      headerName: "Entidad",
      flex: 1,
      minWidth: 180,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        if (params.row.isTotal) return null
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value || "Sin entidad"}</Typography>
          </Box>
        )
      },
    },
    {
      field: "ingreso",
      headerName: "Ingreso",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: false,
      valueGetter: (value, row) => {
        if (!row) return 0
        if (row.isTotal) return row.totalIngresos || 0
        if (row.verEnColumna !== "Ingreso") return 0
        return row.montoTotal || 0
      },
      renderCell: (params) => {
        // Totales
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3",
                  fontSize: "1.05rem",
                }}
              >
                ${formatNumberForDisplay(params.row.totalIngresos, { locale: localeConfig.locale })}
              </Typography>
            </Box>
          )
        }

        if (params.row.verEnColumna !== "Ingreso") return null
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                bgcolor: alpha("#4CAF50", 0.1),
                color: "#4CAF50",
              }}
            >
              ${formatNumberForDisplay(params.row.montoTotal, { locale: localeConfig.locale })}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "egreso",
      headerName: "Egreso",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: false,
      valueGetter: (value, row) => {
        if (!row) return 0
        if (row.isTotal) return row.totalEgresos || 0
        if (row.verEnColumna !== "Egreso") return 0
        return row.montoTotal || 0
      },
      renderCell: (params) => {
        if (params.row.isTotal) {
          return (
            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  color: "#2196F3",
                  fontSize: "1.05rem",
                }}
              >
                ${formatNumberForDisplay(params.row.totalEgresos, { locale: localeConfig.locale })}
              </Typography>
            </Box>
          )
        }

        if (params.row.verEnColumna !== "Egreso") return null
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2">
              ${formatNumberForDisplay(params.row.montoTotal, { locale: localeConfig.locale })}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "tipoTransaccion",
      headerName: "Tipo de Transacción",
      flex: 1,
      minWidth: 180,
      headerAlign: "center",
      sortable: false,
      valueGetter: (value, row) => {
        if (!row) return "N/A"
        return row.tipoTransaccion || "N/A"
      },
      renderCell: (params) => {
        if (params.row.isTotal) return null
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "pagos",
      headerName: "Medio de Pago",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: false,
      valueGetter: (value, row) => {
        if (!row) return "N/A"
        const pagos = Array.isArray(row.pagos) ? row.pagos : []
        if (pagos.length > 1) return "Múltiple"
        return pagos[0]?.descripcion || "N/A"
      },
      renderCell: (params) => {
        if (params.row.isTotal) return null
        const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : []

        if (pagos.length > 1) {
          return <Chip label="Múltiple" size="small" color="info" sx={{ fontWeight: "bold", display: "flex" }} />
        }

        return (
          <Chip
            icon={<PointOfSaleIcon fontSize="small" />}
            label={pagos[0]?.descripcion || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: "#00AAE4",
              borderColor: alpha("#00AAE4", 0.5),
              "& .MuiChip-icon": { color: "#00AAE4" },
              display: "flex",
            }}
          />
        )
      },
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        if (params.row.isTotal) return null
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: "usuario",
      headerName: "Usuario",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        if (params.row.isTotal) return null
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        )
      },
    },
  ], [localeConfig.locale])

  // Efecto para resetear medio de pago seleccionado
  useEffect(() => {
    if (selectedTipoMedioDePago == null) {
      setSelectedMedioDePago("")
    } else {
      getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
    }
  }, [selectedTipoMedioDePago])

  useEffect(() => {
    // console.log("effect page")
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // setLoading(true)
    fetchData()
    setLoading(false)
  }, [page])

  const formatCurrency = (amount) => {
    return `$${formatNumberForDisplay(amount || 0, { locale: localeConfig.locale })}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    return dayjs(dateString).format("DD/MM/YYYY")
  }

  // Preparar datos agrupados con fila de totales
  const datosAgrupadosConTotales = useMemo(() => {
    if (!agruparMediosPago || mediosDePagoAgrupados.length === 0) {
      return []
    }

    // Agregar IDs únicos a cada fila
    const datosConId = mediosDePagoAgrupados.map((mp, index) => ({
      id: `medio-pago-${index}`,
      ...mp,
    }))

    // Calcular totales
    const totalRow = {
      id: "TOTAL_ROW_AGRUPADO",
      medioDePago: "TOTALES",
      totalIngresos: totalIngresos,
      totalEgresos: totalEgresos,
      montoNeto: totalIngresos - totalEgresos,
      isTotal: true,
    }

    return [...datosConId, totalRow]
  }, [agruparMediosPago, mediosDePagoAgrupados, totalIngresos, totalEgresos])

  // Función para generar PDF de la caja
  const generarPDFCaja = async () => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    // Validar que hay datos para generar el PDF
    if (agruparMediosPago) {
      if (!mediosDePagoAgrupados || mediosDePagoAgrupados.length === 0) {
        console.warn("No hay datos agrupados para generar el PDF")
        return
      }
    } else {
      if (!transaccionesPagos || transaccionesPagos.length === 0) {
        console.warn("No hay transacciones para generar el PDF")
        return
      }
    }

    // Cargar logo del tenant
    let logoBase64 = null
    try {
      const params = await getParametrosGlobales(["logoNavbarTenant"])
      logoBase64 = params?.logoNavbarTenant || null
    } catch (_) {}

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const MARGIN_X = 15
    const contentWidth = pageWidth - (MARGIN_X * 2)

    // Colores
    const darkNavy = [26, 26, 26]
    const lightGray = [240, 240, 240]

    // ===========================================
    // ENCABEZADO con logo
    // ===========================================
    const { height: logoH } = drawPdfLogo({ doc, logoBase64, x: MARGIN_X, y: 10, maxW: 28.2, maxH: 12.7 })
    let yPosition = Math.max(20, 10 + logoH + 4)

    doc.setFontSize(18)
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(...darkNavy)
    const tituloPDF = agruparMediosPago ? "Reporte de Caja - Medios de Pago" : "Reporte de Caja"
    doc.text(tituloPDF, pageWidth / 2, yPosition, { align: "center" })
    yPosition += 10

    // Fecha del reporte
    doc.setFontSize(10)
    doc.setFont("Helvetica", "normal")
    doc.setTextColor(100, 100, 100)
    const fechaReporte = startDate.format("DD/MM/YYYY")
    doc.text(`Fecha: ${fechaReporte}`, pageWidth / 2, yPosition, { align: "center" })
    yPosition += 8

    // Filtros aplicados
    doc.setFontSize(9)
    doc.setFont("Helvetica", "italic")
    const filtros = []
    if (selectedUbicacion) filtros.push(`Ubicación: ${selectedUbicacion.descripcion}`)
    if (selectedUsuario) filtros.push(`Usuario: ${selectedUsuario.usuario}`)
    if (selectedTipoMedioDePago) filtros.push(`Tipo Pago: ${selectedTipoMedioDePago.descripcion}`)
    if (selectedMedioDePago && selectedMedioDePago.id) filtros.push(`Medio Pago: ${selectedMedioDePago.descripcion}`)
    
    if (filtros.length > 0) {
      doc.text(`Filtros: ${filtros.join(" | ")}`, pageWidth / 2, yPosition, { align: "center", maxWidth: contentWidth })
      yPosition += 6
    } else {
      doc.text("Filtros: Todos", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 6
    }

    yPosition += 5

    // ===========================================
    // RESUMEN DE CAJA
    // ===========================================
    doc.setFontSize(12)
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(...darkNavy)
    doc.text("Resumen de Caja", pageWidth / 2, yPosition, { align: "center" })
    yPosition += 8

    // Calcular ancho del resumen
    const tableMarginForResumen = 15
    const resumenWidth = 100
    const resumenX = (pageWidth - resumenWidth) / 2
    const resumenHeight = cajaData && cajaData.ultimaCaja ? 40 : 30

    doc.setFontSize(9)
    doc.setFont("Helvetica", "normal")
    doc.setTextColor(60, 60, 60)

    let resumenY = yPosition
    const resumenTextX = resumenX + 8

    // Monto Inicial
    if (cajaData && cajaData.ultimaCaja) {
      doc.setFont("Helvetica", "bold")
      doc.text("Monto Inicial:", resumenTextX, resumenY)
      doc.setFont("Helvetica", "normal")
      const montoInicial = cajaData.ultimaCaja.montoCierre || 0
      doc.text(
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(montoInicial),
        resumenX + resumenWidth - 8,
        resumenY,
        { align: "right" }
      )
      resumenY += 7
    }

    // Total Ingresos
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(76, 175, 80) // Verde
    doc.text("Total Ingresos:", resumenTextX, resumenY)
    doc.setFont("Helvetica", "normal")
    doc.text(
      new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(totalIngresos),
      resumenX + resumenWidth - 8,
      resumenY,
      { align: "right" }
    )
    resumenY += 7

    // Total Egresos
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(244, 67, 54) // Rojo
    doc.text("Total Egresos:", resumenTextX, resumenY)
    doc.setFont("Helvetica", "normal")
    doc.text(
      new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(totalEgresos),
      resumenX + resumenWidth - 8,
      resumenY,
      { align: "right" }
    )
    resumenY += 7

    // Monto Final
    doc.setFont("Helvetica", "bold")
    doc.setTextColor(...darkNavy)
    doc.setFontSize(10)
    doc.text("Monto Final:", resumenTextX, resumenY)
    doc.text(
      new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(cajaMontoFinal),
      resumenX + resumenWidth - 8,
      resumenY,
      { align: "right" }
    )

    yPosition = resumenY + 12

    // ===========================================
    // TABLA
    // ===========================================
    const tableMarginX = 0

    if (agruparMediosPago) {
      // --- TABLA AGRUPADA POR MEDIOS DE PAGO ---
      doc.setFontSize(12)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Resumen por Medio de Pago", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      const formatMonto = (valor) =>
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(valor || 0)

      const tableDataAgrupada = mediosDePagoAgrupados.map((mp) => [
        mp.medioDePago || "N/A",
        formatMonto(mp.totalIngresos),
        formatMonto(mp.totalEgresos),
        formatMonto(mp.montoNeto),
      ])

      // Fila de totales
      tableDataAgrupada.push([
        "TOTALES",
        formatMonto(totalIngresos),
        formatMonto(totalEgresos),
        formatMonto(totalIngresos - totalEgresos),
      ])

      doc.autoTable({
        startY: yPosition,
        head: [["Medio de Pago", "Ingreso Total", "Egreso Total", "Monto Neto"]],
        body: tableDataAgrupada,
        theme: "striped",
        headStyles: {
          fillColor: darkNavy,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [60, 60, 60],
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        columnStyles: {
          0: { cellWidth: "auto", halign: "left", minCellWidth: 50 },
          1: { cellWidth: "auto", halign: "right", minCellWidth: 40 },
          2: { cellWidth: "auto", halign: "right", minCellWidth: 40 },
          3: { cellWidth: "auto", halign: "right", minCellWidth: 40 },
        },
        styles: {
          cellPadding: 4,
          overflow: "linebreak",
        },
        didParseCell: function (data) {
          if (data.row.index === tableDataAgrupada.length - 1) {
            data.cell.styles.fillColor = [219, 234, 254]
            data.cell.styles.fontStyle = "bold"
            data.cell.styles.fontSize = 10
            data.cell.styles.textColor = [33, 150, 243]
          }
        },
        margin: { left: tableMarginX, right: tableMarginX },
      })
    } else {
      // --- TABLA NORMAL DE TRANSACCIONES ---
      doc.setFontSize(12)
      doc.setFont("Helvetica", "bold")
      doc.setTextColor(...darkNavy)
      doc.text("Transacciones", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 8

      const transaccionesParaTabla = Array.isArray(transaccionesPagos)
        ? transaccionesPagos
        : []

      let totalIngresosPaginaPDF = 0
      let totalEgresosPaginaPDF = 0
      
      transaccionesParaTabla.forEach((transaccion) => {
        if (transaccion.operacionCaja === "+") {
          totalIngresosPaginaPDF += transaccion.montoTotal
        } else if (transaccion.operacionCaja === "-") {
          totalEgresosPaginaPDF += transaccion.montoTotal
        }
      })

      const tableData = transaccionesParaTabla.map((transaccion) => {
        const ingreso = transaccion.operacionCaja === "+" 
          ? new Intl.NumberFormat("es-AR", {
              style: "currency",
              currency: "ARS",
              minimumFractionDigits: 2,
            }).format(transaccion.montoTotal)
          : "-"
        
        const egreso = transaccion.operacionCaja === "-"
          ? new Intl.NumberFormat("es-AR", {
              style: "currency",
              currency: "ARS",
              minimumFractionDigits: 2,
            }).format(transaccion.montoTotal)
          : "-"

        const medioPago = transaccion.pagos && transaccion.pagos.length > 0
          ? transaccion.pagos[0].descripcion
          : "N/A"

        return [
          transaccion.id || "",
          transaccion.createdAt || "",
          ingreso,
          egreso,
          medioPago,
          transaccion.ubicacion || "",
          transaccion.usuario || "",
        ]
      })

      tableData.push([
        "TOTALES",
        "",
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(totalIngresosPaginaPDF),
        new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(totalEgresosPaginaPDF),
        "",
        "",
        "",
      ])

      doc.autoTable({
        startY: yPosition,
        head: [["ID", "Fecha", "Ingreso", "Egreso", "Medio de Pago", "Ubicación", "Usuario"]],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: darkNavy,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [60, 60, 60],
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        columnStyles: {
          0: { cellWidth: "auto", halign: "center", minCellWidth: 28 },
          1: { cellWidth: "auto", halign: "center", minCellWidth: 38 },
          2: { cellWidth: "auto", halign: "right", minCellWidth: 28 },
          3: { cellWidth: "auto", halign: "right", minCellWidth: 28 },
          4: { cellWidth: "auto", halign: "center", minCellWidth: 30 },
          5: { cellWidth: "auto", halign: "center", minCellWidth: 25 },
          6: { cellWidth: "auto", halign: "center", minCellWidth: 25 },
        },
        styles: {
          cellPadding: 3,
          overflow: "linebreak",
          halign: "left",
        },
        didParseCell: function (data) {
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fillColor = [219, 234, 254]
            data.cell.styles.fontStyle = "bold"
            data.cell.styles.fontSize = 9
          }
        },
        margin: { left: tableMarginX, right: tableMarginX },
      })
    }

    // Obtener la posición Y final después de la tabla
    const finalY = doc.lastAutoTable.finalY || yPosition + 50

    // ===========================================
    // PIE DE PÁGINA
    // ===========================================
    const pieY = pageHeight - 20
    doc.setFontSize(8)
    doc.setFont("Helvetica", "normal")
    doc.setTextColor(120, 120, 120)
    
    const fechaHoraGeneracion = new Date().toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    
    doc.text(
      `Generado el ${fechaHoraGeneracion} por ${user?.usuario || "Usuario"}`,
      pageWidth / 2,
      pieY,
      { align: "center" }
    )

    // Número de página
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      )
    }

    // Descargar PDF
    const sufijo = agruparMediosPago ? "_MediosDePago" : ""
    const nombreArchivo = `Reporte_Caja${sufijo}_${fechaReporte.replace(/\//g, "-")}.pdf`
    doc.setProperties({ title: nombreArchivo, subject: "Reporte de Caja", creator: "Sistema" })
    deliverPdf({ doc, fileName: nombreArchivo, targetWindow: deliveryContext.targetWindow })
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", p: 2 }}>
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderRadius: 3,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          mb: 1, // Changed margin-bottom to 1 from 2
        }}
      >
        {/* Left section: Date picker */}
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", width: "100%", paddingRight: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={decreaseDate} size="small" sx={{ color: "#5b5b5f" }}>
                <ArrowBackIcon />
              </IconButton>
              <DatePicker
                label="Fecha Desde"
                format="DD/MM/YYYY"
                disableFuture
                maxDate={dayjs()}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: { size: "small", inputProps: { maxLength: 10 } },
                }}
                sx={{ width: 150 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    sx={{ minWidth: 150 }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon sx={{ color: "rgb(244, 177, 14)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <IconButton onClick={increaseDate} size="small" sx={{ color: "#5b5b5f" }}>
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          </LocalizationProvider>

          
          {/* Ubicación and Usuario filters always visible in main menu */}
          <Autocomplete
            size="small"
            disablePortal
            options={ubicacionesNoDestino}
            getOptionLabel={(option) => option.descripcion || ""}
            value={selectedUbicacion}
            sx={{ flex: 0.5, minWidth: 150 }}
            onChange={(event, newValue) => {
              setSelectedUbicacion(newValue)
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ubicación"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Autocomplete
            size="small"
            disablePortal
            options={usuarios}
            getOptionLabel={(option) => option.usuario || ""}
            value={selectedUsuario}
            onChange={(event, newValue) => {
              setSelectedUsuario(newValue)
            }}
            sx={{ flex: 0.5, minWidth: 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuario"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "rgb(244, 177, 14)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", marginRight: 3 }}>
          <Tooltip title="Medios de pago">
            <IconButton
              onClick={() => setShowPaymentFilters(!showPaymentFilters)}
              size="medium"
              sx={{
                color: "#5b5b5f",
                border: `1px solid ${alpha("#5b5b5f", showPaymentFilters ? 0.5 : 0.3)}`,
                bgcolor: showPaymentFilters ? alpha("rgb(244, 177, 14)", 0.1) : "transparent",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  bgcolor: alpha("rgb(244, 177, 14)", 0.15),
                  borderColor: "rgb(244, 177, 14)",
                },
              }}
            >
              <AttachMoneyIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Limpiar filtros">
            <IconButton
              onClick={() => {
                setSelectedTipoMedioDePago(null)
                setSelectedMedioDePago([])
                setSelectedUbicacion(null)
                setSelectedUsuario(null)
                setStartDate(dayjs())
                setEndDate(dayjs())
              }}
              size="small"
              sx={{
                color: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                borderRadius: 1.5,
                p: 0.75,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.error.main, 0.15),
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <FilterAltOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsModalOpen(true)}
            startIcon={<ReceiptIcon />}
            sx={{
              borderColor: "#5b5b5f",
              color: "#5b5b5f",
              padding: "8px 16px",
              textTransform: "none",
              "&:hover": {
                borderColor: "rgb(244, 177, 14)",
                color: "rgb(244, 177, 14)",
              },
            }}
          >
            Agregar transacción
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={fetchData}
            startIcon={<SearchIcon />}
            sx={{
              backgroundColor: "#5b5b5f",
              textTransform: "none",
              padding: "8px 16px",
              color: "white",
              "&:hover": {
                backgroundColor: alpha("rgb(244, 177, 14)", 0.8),
              },
            }}
          >
            Buscar
          </Button>

          <Tooltip title="Descargar PDF Caja">
            <span>
              <IconButton
                onClick={generarPDFCaja}
                disabled={loading || !transaccionesPagos || transaccionesPagos.length === 0}
                sx={{
                  color: "#5b5b5f",
                  "&:hover": {
                    color: "rgb(244, 177, 14)",
                  },
                  "&:disabled": {
                    color: alpha("#5b5b5f", 0.3),
                  },
                }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* Resumen de Caja */}
        {mostrarMontoInicial && cajaData && cajaData.ultimaCaja && (
            <Paper
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: "#5b5b5f",
                color: "white",
                minWidth: 200,
                textAlign: "center",
                boxShadow: "0px 4px 12px rgba(244, 177, 14, 0.3)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Monto Inicial:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {formatCurrency(cajaData?.ultimaCaja?.montoCierre || 0)}
              </Typography>
            </Paper>
          )}

      </Paper>

      <Collapse in={showPaymentFilters}>
        <Paper
          sx={{
            p: 2,
            mb: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            {/* Dropdowns de medios de pago - reducidos a la mitad */}
            <Autocomplete
              size="small"
              sx={{ minWidth: 200, width: 200 }}
              disablePortal
              options={tipoMedioDePago}
              getOptionLabel={(option) => option.descripcion || ""}
              value={selectedTipoMedioDePago}
              onChange={(event, newValue) => {
                setSelectedTipoMedioDePago(newValue)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo de medio de pago"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              size="small"
              sx={{ minWidth: 200, width: 200 }}
              disablePortal
              options={medioDePagoIdTipoEspecifico}
              getOptionLabel={(option) => option.descripcion || ""}
              value={selectedMedioDePago || ""}
              onChange={(event, newValue) => {
                setSelectedMedioDePago(newValue)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Medio de pago específico"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PointOfSaleIcon sx={{ color: "rgb(244, 177, 14)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            {/* Checkbox para agrupar medios de pago */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agruparMediosPago}
                  onChange={(e) => setAgruparMediosPago(e.target.checked)}
                  sx={{
                    color: "rgb(244, 177, 14)",
                    "&.Mui-checked": {
                      color: "rgb(244, 177, 14)",
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: "500" }}>
                  Agrupar medios de pago
                </Typography>
              }
              sx={{ ml: 2 }}
            />
          </Box>
        </Paper>
      </Collapse>

      {/* Contenido principal */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <SpinnerLading />
        </Box>
      ) : transaccionesPagos.length === 0 ? (
        /* Estado vacío - Sin datos */
        <Paper
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            p: 4,
            textAlign: "center",
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 80, color: alpha("#5b5b5f", 0.3), mb: 2 }} />
          <Typography variant="h5" sx={{ color: "#5b5b5f", mb: 1, fontWeight: "bold" }}>
            No se encontraron transacciones
          </Typography>
          <Typography variant="body1" sx={{ color: alpha("#5b5b5f", 0.7), mb: 3, maxWidth: 400 }}>
            No hay transacciones de pago para los filtros seleccionados. Intente ajustar las fechas o los filtros de
            medio de pago.
          </Typography>
          <Button
            variant="contained"
            onClick={fetchData}
            startIcon={<ReplayIcon />}
            sx={{
              backgroundColor: "#5b5b5f",
              color: "white",
              textTransform: "none",
              px: 3,
              "&:hover": {
                backgroundColor: alpha("rgb(244, 177, 14)", 0.8),
              },
            }}
          >
            Actualizar Búsqueda
          </Button>
        </Paper>
      ) : (
        /* Tabla con datos */
        <>
          <Paper
            sx={{
              flex: 1,
              borderRadius: 3,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <DataGrid
              key={`${isNavbarCollapsed ? "collapsed" : "expanded"}-${agruparMediosPago ? "agrupado" : "normal"}`}
              className="tablaVentasDataGrid"
              rows={agruparMediosPago ? datosAgrupadosConTotales : transaccionesPagos}
              columns={agruparMediosPago ? columnsAgrupadas : columns}
              getRowId={(row) => row.id}
              pagination={!agruparMediosPago}
              paginationMode={agruparMediosPago ? "client" : "server"}
              sortingMode={agruparMediosPago ? "client" : "server"}
              sortingOrder={["desc", "asc"]}
              sortModel={agruparMediosPago ? [] : sortModel}
              onSortModelChange={agruparMediosPago ? undefined : handleSortModelChange}
              getRowClassName={(params) => (params.row.isTotal ? "total-row" : "")}
              getRowHeight={() => 35}
              headerHeight={35}
              sx={{
                width: "100%",
                border: 0,
                height: "100%",
                "& .MuiDataGrid-footerContainer": {
                  display: "none !important",
                },
                "& .total-row": {
                  backgroundColor: alpha("#2196F3", 0.08),
                  borderTop: `2px solid ${alpha("#2196F3", 0.3)}`,
                  "&:hover": {
                    backgroundColor: alpha("#2196F3", 0.12),
                  },
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#5b5b5f",
                  color: "white",
                  borderBottom: "2px solid #333",
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeader": {
                  height: "45px !important",
                  minHeight: "45px !important",
                  maxHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "white !important",
                  lineHeight: "45px !important",
                },
                "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                  color: "white !important",
                },
                "& .MuiDataGrid-menuIconButton": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiInputBase-root": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiTablePagination-root": {
                  color: "black !important",
                },
                "& .MuiSelect-icon": {
                  color: "rgb(244, 177, 14) !important",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                "& .MuiDataGrid-row": {
                  "&:nth-of-type(even)": {
                    backgroundColor: "#F7F7F7",
                  },
                  "&:hover": {
                    backgroundColor: "#E3EAFD !important",
                    transition: "background 0.3s ease",
                  },
                },
                "& .row-devolucion": {
                  backgroundColor: alpha("#9e9e9e !important", 0.05),
                  "&:hover": {
                    backgroundColor: alpha("#9e9e9e !important", 0.1),
                  },
                },
                "& .MuiDataGrid-virtualScroller": {
                  lineHeight: "none !important",
                  height: "10px !imporant",
                },
                "& .MuiDataGrid-footerContainer": {
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "#ffffff",
                  borderTop: "1px solid #ddd",
                  zIndex: 10,
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                  width: "2px",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "10px",
                },
                "& .MuiDataGrid-toolbarContainer": {
                  justifyContent: "flex-end",
                  borderBottom: "1px solid #e0e0e0",
                },
              }}
              {...(!agruparMediosPago && {
                columnVisibilityModel,
                onColumnVisibilityModelChange: (newModel) => setColumnVisibilityModel(newModel),
              })}
              components={{
                Toolbar: agruparMediosPago ? undefined : ColumnSelectorToolbar,
                Pagination: () => null,
              }}
            />
          </Paper>

          {/* Footer con paginación */}
          <Box sx={{ flexShrink: 0, pt: 2 }}>
            <CustomPagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              montoTotalTransacciones={montoTotalTransacciones}
              vieneDe={"tablaCaja"}
              montoFinalCaja={cajaMontoFinal}
              mostrarMontoFinal={mostrarMontoFinal}
              totalIngresos={totalIngresos}
              totalEgresos={totalEgresos}
              onPageChange={(newPage) => setPage(newPage)}
            />
          </Box>
        </>
      )}

      {/* MODAL PARA AGREGAR TRANSACCIÓN */}
      <TransaccionModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fechaTransaccion={startDate}
        onTransactionAdded={handleTransactionAdded}
      />
    </Box>
  )
}

export default CajaComponent
